import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { 
  MagnifyingGlassIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckIcon
} from '@/components/icons';

import type { FilterControlProps, DateRange } from '@/types/filters';

const localDateInput = (value: Date | string | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Filter Control Component
 * 
 * Renders different types of filter inputs based on configuration:
 * - Search inputs
 * - Single/Multi-select dropdowns
 * - Date range pickers
 * - Toggle switches
 * - Checkboxes
 * - Sliders
 */
export function FilterControl({
  config,
  value,
  onChange,
  options = [],
  loading = false,
  className,
  disabled = false,
  onSearch
}: FilterControlProps) {

  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const customDateRange: DateRange = config.type === 'dateRange' && value && typeof value === 'object'
    ? value as DateRange
    : {};
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setDropdownOpen(false);
      dropdownTriggerRef.current?.focus();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [dropdownOpen]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    
    return options.filter(option =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  // Handle search input
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    if (onSearch && query.length > 2) {
      try {
        await onSearch(query);
      } catch (err) {
        console.error('Filter search error:', err);
      }
    }
  }, [onSearch]);

  // Handle option selection for multi-select
  const handleOptionToggle = useCallback((optionValue: unknown) => {
    if (config.multiple) {
      const currentValues: unknown[] = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(optionValue);

      const newValues = isSelected
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];

      onChange(newValues);
    } else {
      onChange(optionValue);
      setDropdownOpen(false);
    }
  }, [config.multiple, value, onChange]);

  // Handle date range change
  const handleDateRangeChange = useCallback((newRange: DateRange) => {
    onChange(newRange);
  }, [onChange]);

  // Get display value for select inputs
  const getDisplayValue = () => {
    if (config.type === 'multiselect') {
      const selectedOptions = options.filter(opt => 
        Array.isArray(value) && value.includes(opt.value)
      );
      
      const defaultPlaceholder = config.placeholder || (config.title ? `Select ${config.title}...` : 'Select...');
      if (selectedOptions.length === 0) return defaultPlaceholder;
      if (selectedOptions.length === 1) return selectedOptions[0].label;
      return `${selectedOptions.length} selected`;
    }
    
    if (config.type === 'select') {
      const defaultPlaceholder = config.placeholder || (config.title ? `Select ${config.title}...` : 'Select...');
      const selectedOption = options.find(opt => opt.value === value);
      return selectedOption ? selectedOption.label : defaultPlaceholder;
    }
    
    return config.placeholder || (config.title ? `Select ${config.title}...` : '');
  };

  // Render based on filter type
  switch (config.type) {
    case 'search':
      return (
        <div className={clsx('relative', className)}>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={config.placeholder}
              disabled={disabled}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
            {value && (
              <button
                aria-label={config.title ? `Clear ${config.title}` : 'Clear filter'}
                onClick={() => onChange('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      );

    case 'select':
    case 'multiselect':
      return (
        <div className={clsx('relative', className)}>
          <button
            ref={dropdownTriggerRef}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            disabled={disabled || loading}
            aria-expanded={dropdownOpen}
            aria-haspopup="listbox"
            className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          >
            <span className="truncate">
              {loading ? 'Loading...' : getDisplayValue()}
            </span>
            <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-2" />
          </button>

          {dropdownOpen && !disabled && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
              {(config.searchable && options.length > 5) && (
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-10 pr-4 py-1 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              )}
              
              <div className="max-h-60 overflow-y-auto" role="listbox" aria-multiselectable={config.multiple || undefined}>
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = config.multiple
                      ? Array.isArray(value) && value.includes(option.value)
                      : value === option.value;
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleOptionToggle(option.value)}
                        disabled={option.disabled}
                        role="option"
                        aria-selected={isSelected}
                        className={clsx(
                          'w-full flex items-center px-3 py-2 text-sm text-left hover:bg-gray-50',
                          'focus:bg-gray-50 focus:outline-none',
                          isSelected && 'bg-primary-50 text-primary-700',
                          option.disabled && 'text-gray-400 cursor-not-allowed'
                        )}
                      >
                        {config.multiple && (
                          <div className={clsx(
                            'mr-3 w-4 h-4 border rounded flex items-center justify-center',
                            isSelected 
                              ? 'bg-primary-600 border-primary-600' 
                              : 'border-gray-300'
                          )}>
                            {isSelected && (
                              <CheckIcon className="w-3 h-3 text-white" />
                            )}
                          </div>
                        )}
                        
                        {option.icon && (
                          <div className="mr-3 flex-shrink-0">
                            {option.icon}
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <span className="truncate">{option.label}</span>
                            {option.count !== undefined && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({option.count})
                              </span>
                            )}
                          </div>
                          {option.description && (
                            <div className="text-xs text-gray-500 truncate">
                              {option.description}
                            </div>
                          )}
                        </div>
                        
                        {option.color && (
                          <div 
                            className="ml-3 w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: option.color }}
                          />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
              
              {config.multiple && Array.isArray(value) && value.length > 0 && (
                <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={() => onChange([])}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Clear all selections
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Backdrop to close dropdown */}
          {dropdownOpen && (
            <div
              className="fixed inset-0 z-5"
              onClick={() => setDropdownOpen(false)}
              aria-hidden="true"
            />
          )}
        </div>
      );

    case 'toggle':
      return (
        <div className={clsx('flex items-center', className)}>
          <button
            onClick={() => onChange(!value)}
            disabled={disabled}
            className={clsx(
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              value ? 'bg-primary-600' : 'bg-gray-200',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            role="switch"
            aria-checked={value}
            aria-labelledby={`toggle-${config.id}`}
          >
            <span
              className={clsx(
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                value ? 'translate-x-5' : 'translate-x-0'
              )}
            />
          </button>
          <label id={`toggle-${config.id}`} className="ml-3 text-sm text-gray-700">
            {config.title}
          </label>
        </div>
      );

    case 'checkbox':
      return (
        <div className={clsx('flex items-center', className)}>
          <input
            id={`checkbox-${config.id}`}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked ? true : null)}
            disabled={disabled}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor={`checkbox-${config.id}`} className="ml-3 text-sm text-gray-700">
            {config.title}
          </label>
        </div>
      );

    case 'dateRange':
      return (
        <div className={clsx('space-y-3', className)}>
          {/* Custom Date Range */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-700">Custom Range</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={localDateInput(customDateRange.from)}
                  onChange={(e) => {
                    const newRange = { 
                      ...customDateRange, 
                      from: e.target.value ? new Date(`${e.target.value}T00:00:00`) : undefined,
                      preset: undefined 
                    };
                    handleDateRangeChange(newRange);
                  }}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={localDateInput(customDateRange.to)}
                  onChange={(e) => {
                    const newRange = { 
                      ...customDateRange, 
                      to: e.target.value ? new Date(`${e.target.value}T00:00:00`) : undefined,
                      preset: undefined 
                    };
                    handleDateRangeChange(newRange);
                  }}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>
      );

    case 'slider':
      return (
        <div className={clsx('space-y-3', className)}>
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>{config.title}</span>
            <span>
              {Array.isArray(value) ? `${value[0]} - ${value[1]}` : '0 - 100'}
            </span>
          </div>
          <div className="px-2">
            <input
              type="range"
              min="0"
              max="100" 
              value={Array.isArray(value) ? value[0] : 0}
              onChange={(e) => {
                const newValue = [parseInt(e.target.value), Array.isArray(value) ? value[1] : 100];
                onChange(newValue);
              }}
              disabled={disabled}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>
      );

    case 'userSelect':
    case 'companySelect':
    case 'tagSelect':
      // These use the same select logic but with different option sources
      return (
        <div className={clsx('relative', className)}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            disabled={disabled || loading}
            className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          >
            <span className="truncate">
              {loading ? 'Loading...' : getDisplayValue()}
            </span>
            <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-2" />
          </button>

          {dropdownOpen && !disabled && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
              {config.searchable && (
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-10 pr-4 py-1 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              )}
              
              <div className="max-h-60 overflow-y-auto">
                {filteredOptions.map((option) => {
                  const isSelected = config.multiple
                    ? Array.isArray(value) && value.includes(option.value)
                    : value === option.value;
                  
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleOptionToggle(option.value)}
                      className={clsx(
                        'w-full flex items-center px-3 py-2 text-sm text-left hover:bg-gray-50',
                        'focus:bg-gray-50 focus:outline-none',
                        isSelected && 'bg-primary-50 text-primary-700'
                      )}
                    >
                      {config.multiple && (
                        <div className={clsx(
                          'mr-3 w-4 h-4 border rounded flex items-center justify-center',
                          isSelected 
                            ? 'bg-primary-600 border-primary-600' 
                            : 'border-gray-300'
                        )}>
                          {isSelected && (
                            <CheckIcon className="w-3 h-3 text-white" />
                          )}
                        </div>
                      )}
                      
                      {option.icon && (
                        <div className="mr-3 flex-shrink-0">
                          {option.icon}
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <span className="truncate">{option.label}</span>
                          {option.count !== undefined && (
                            <span className="ml-2 text-xs text-gray-500">
                              ({option.count})
                            </span>
                          )}
                        </div>
                        {option.description && (
                          <div className="text-xs text-gray-500 truncate">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );

    default:
      return (
        <div className={clsx('text-sm text-gray-500', className)}>
          Unsupported filter type: {config.type}
        </div>
      );
  }
}
